/* eslint-disable react/prop-types */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TableUtility } from '@utility/dynamic-table';
import { toast } from 'react-toastify';
import { Skeleton } from 'primereact/skeleton';
import './MilestoneMis.scss';
import loadingPuzzleGif from '../../../Assets/images/svg/loading puzzle.gif';
import MilestoneInitial from './MilestoneInitial';
import ChangeMilestoneModal from './ChangeMilestoneModal';
import { appendServiceNameToURL, appendServicePortToURL } from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { MIS_MILESTONE, MIS_MONTHLY_MILESTONE, MILESTONE_ORPHAN_INVOICES } from '../../../Api/resources/endPoint';
import { useAuth } from '../../../Contexts/AuthContext';

const getMilestoneApiUrl = () => {
  const serviceURL = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${MIS_MILESTONE}`;
  }
  return `${baseUrl}:${servicePort}${serviceURL}/api${MIS_MILESTONE}`;
};

const getMonthlyMilestoneApiUrl = () => {
  const serviceURL = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${MIS_MONTHLY_MILESTONE}`;
  }
  return `${baseUrl}:${servicePort}${serviceURL}/api${MIS_MONTHLY_MILESTONE}`;
};

const getOrphanMilestoneApiUrl = () => {
  const serviceURL = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${MILESTONE_ORPHAN_INVOICES}`;
  }
  return `${baseUrl}:${servicePort}${serviceURL}/api${MILESTONE_ORPHAN_INVOICES}`;
};

const TABLES_CONFIG = [
  {
    apiUrl: getMonthlyMilestoneApiUrl(),
    toolBarHeading: 'Monthly Milestone MIS',
    useCustomBodyRenderer: true,
  },
  {
    apiUrl: getMilestoneApiUrl(),
    toolBarHeading: 'Milestone MIS',
  },
  {
    apiUrl: getOrphanMilestoneApiUrl(),
    toolBarHeading: 'Invoices Without Planned Milestone',
  },
];

const getMilestoneExportFileName = (toolBarHeading = 'Milestone MIS') => (
  String(toolBarHeading || 'Milestone MIS').replace(/\s+/g, '_')
);

const buildProjectDetailsUrl = (projectId) => (
  `/delivery/project-details?projectId=${encodeURIComponent(String(projectId))}`
);

const needsMilestoneRegrouping = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  for (let index = 0; index < rows.length; index++) {
    const row = rows[index];
    if (row?.is_child_row) {
      const prevRow = index > 0 ? rows[index - 1] : null;
      if (!prevRow || prevRow.milestone_id !== row.milestone_id || prevRow.is_child_row) {
        return true;
      }
    }
  }
  return false;
};

const regroupMilestoneData = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const milestoneGroups = new Map();
  const milestoneOrder = [];
  const totalRow = rows.find((row) => row.id === 'totals-row' || row.project_name === 'Total');
  const dataRows = rows.filter((row) => row.id !== 'totals-row' && row.project_name !== 'Total');

  dataRows.forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const milestoneId = row?.milestone_id;
    if (!milestoneId) return;
    if (!milestoneGroups.has(milestoneId)) {
      milestoneGroups.set(milestoneId, { parentRow: null, childRows: [] });
      milestoneOrder.push(milestoneId);
    }
    const group = milestoneGroups.get(milestoneId);
    if (row?.is_child_row) {
      group.childRows.push(row);
    } else {
      group.parentRow = row;
    }
  });

  const regroupedRows = [];
  milestoneOrder.forEach((milestoneId) => {
    const group = milestoneGroups.get(milestoneId);
    if (group.parentRow) {
      regroupedRows.push(group.parentRow);
    }
    regroupedRows.push(...group.childRows);
  });

  if (totalRow) {
    regroupedRows.push(totalRow);
  }
  return regroupedRows;
};

// Dynamic Skeleton Loader matching expected table count
const LocalSkeleton = ({ count = 1 }) => (
  <div className="skeleton-loader">
    {Array.from({ length: count }).map((val, index) => (
      <div key={index} className='skeleton-card'>
        <div className='skeleton-header'>
          <Skeleton width="180px" height="1.5rem" />
          <div className="skeleton-header-actions">
            <Skeleton width="80px" height="1.5rem" />
            <Skeleton width="80px" height="1.5rem" />
          </div>
        </div>
        <div className="skeleton-body-content">
          <Skeleton width="100%" height="2rem" />
          <Skeleton width="100%" height="2rem" />
          <Skeleton width="100%" height="2rem" />
          <Skeleton width="100%" height="2rem" />
          <Skeleton width="100%" height="2rem" />
        </div>
      </div>
    ))}
  </div>
);

const MisMilestone = () => {
  const containerRef = useRef(null);
  const { userProfile } = useAuth();
  const userProfileRef = useRef(userProfile);
  userProfileRef.current = userProfile;

  const getPermissionActions = useCallback((moduleName) => {
    const permissions = userProfile?.permissions || {};
    const moduleCandidates = [
      moduleName,
      moduleName.replace(/-/g, '_'),
      moduleName.replace(/_/g, '-'),
    ];
    const matchedKey = moduleCandidates.find((candidate) => Array.isArray(permissions[candidate]));
    return Array.isArray(permissions[matchedKey])
      ? permissions[matchedKey].map((action) => String(action || '').trim()
        .toLowerCase())
      : [];
  }, [userProfile]);

  // ==========================================
  // PERMISSIONS CHECK
  // ==========================================
  const isSuperAdmin = useMemo(() => {
    return userProfile?.roleDetails?.some((role) =>
      role.roleName?.toLowerCase().includes('super admin'),
    );
  }, [userProfile]);

  const misPermissions = getPermissionActions('mis:milestone-mis');
  const financeMilestonePerms = getPermissionActions('finance:milestone');

  // Helper variables for clean logic
  const hasMisCreate = misPermissions.includes('create') || misPermissions.includes('add');
  const hasMisUpdate = misPermissions.includes('update') || misPermissions.includes('edit');
  const hasFinanceCreate = financeMilestonePerms.includes('create') || financeMilestonePerms.includes('add');
  const hasFinanceUpdate = financeMilestonePerms.includes('update') || financeMilestonePerms.includes('edit');

  // Checkboxes only require MIS update permissions
  const canBulkUpdate = isSuperAdmin || hasMisUpdate;

  // Action Column Permissions
  const canAddMilestone = isSuperAdmin || (hasMisCreate && hasFinanceCreate);
  const canEditMilestone = isSuperAdmin || (hasMisUpdate && hasFinanceUpdate);
  const canShowMilestoneActions = canAddMilestone || canEditMilestone;

  // GRANULAR PERMISSION CHECKS
  const checkPerm = useCallback((key) => {
    return isSuperAdmin || (userProfile?.permissions?.[key] && userProfile?.permissions?.[key].length > 0);
  }, [isSuperAdmin, userProfile]);

  const hasAmountViewAccess = checkPerm('finance:milestone:milestone-amount-view');
  const hasInvoiceAmountViewAccess = checkPerm('finance:milestone:milestone-invoice-amount-view');
  const hasPoNumberViewAccess = checkPerm('finance:milestone:po-number-view');
  const hasPoLinkViewAccess = checkPerm('finance:milestone:po-link-view');
  const hasReplannedDueDateViewAccess = checkPerm('finance:milestone:replanned-due-date-view');
  const hasReceivedDateViewAccess = checkPerm('finance:milestone:milestone-received-date-view');
  const hasRemarksViewAccess = checkPerm('finance:milestone:milestone-remarks-view');

  const [tables, setTables] = useState([
    { configuration: null, data: [], toolBarHeading: 'Monthly Milestone MIS', rawTableData: [] },
    { configuration: null, data: [], toolBarHeading: 'Milestone MIS', rawTableData: [] },
  ]);
  const [loading, setLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const selectedRowsRef = useRef([]);
  const [rowsToUpdate, setRowsToUpdate] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [milestoneStatuses, setMilestoneStatuses] = useState([]);
  const [invoiceStatuses, setInvoiceStatuses] = useState([]);
  const filterLookupOptionsRef = useRef({});

  const [globalFilters, setGlobalFilters] = useState([]);
  const [globalSort, setGlobalSort] = useState(null);
  const lastAppliedFiltersRef = useRef([]);
  const globalSortRef = useRef(globalSort);
  globalSortRef.current = globalSort;
  const requestIdRef = useRef(0);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    const fetchDropdownFilters = async () => {
      try {
        const baseUrl = getMilestoneApiUrl().replace(MIS_MILESTONE, '');
        const token = userProfile?.access_token || localStorage.getItem('authToken');

        // Fetch all lookup dictionaries for the filter mapping
        const allFiltersRes = await fetch(`${baseUrl}${MIS_MILESTONE}/filters/all`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            token,
          },
          body: JSON.stringify({ userInfo: userProfile }),
        });
        const allFiltersJson = await allFiltersRes.json();
        const data = allFiltersJson?.data || {};

        filterLookupOptionsRef.current = data;
        setMilestoneStatuses(data.milestoneStatuses || []);
        setInvoiceStatuses(data.invoiceStatuses || []);
      } catch (error) {
        console.error('Error fetching statuses and lookup options', error);
      }
    };
    if (userProfile?.email) {
      fetchDropdownFilters();
    }
  }, [userProfile]);

  useEffect(() => {
    if (userProfile?.email && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      handleGlobalFilterApply([]);
    }
  }, [userProfile?.email]);

  const handleActionClick = useCallback((params) => {
    const { action, rowData, fieldPath } = params || {};
    if ((fieldPath === 'project_name' || fieldPath === 'projectName') && rowData) {
      const projectId = rowData.project_id || rowData.projectId;
      if (!projectId) {
        toast.error('Project ID not found for this record.');
        return;
      }
      window.open(buildProjectDetailsUrl(projectId), '_blank', 'noopener,noreferrer');
      return;
    }
    if (action === 'edit' && rowData) {
      if (!canEditMilestone) {
        toast.error('You do not have permission to edit milestones.');
        return;
      }
      const projectId = rowData.project_id || rowData.projectId;
      const projectName = rowData.project_name || rowData.projectName || '';
      const milestoneId = rowData.milestone_id || rowData.milestoneId;
      const invoiceId = rowData.invoice_id || rowData.invoiceId;
      if (projectId) {
        const urlParams = new URLSearchParams();
        urlParams.append('projectId', projectId);
        if (projectName) urlParams.append('projectName', projectName);
        if (milestoneId) {
          urlParams.append('editType', 'milestone');
          urlParams.append('milestoneId', milestoneId);
        } else if (invoiceId) {
          urlParams.append('editType', 'invoice');
          urlParams.append('invoiceId', invoiceId);
        }
        window.open(`/finance/milestone-details?${urlParams.toString()}`, '_blank');
      } else {
        toast.error('Project ID not found for this record.');
      }
      return;
    }
    if (action === 'add' && rowData) {
      if (!canAddMilestone) {
        toast.error('You do not have permission to add invoices.');
        return;
      }
      const projectId = rowData.project_id || rowData.projectId;
      const projectName = rowData.project_name || rowData.projectName || '';
      const milestoneId = rowData.milestone_id || rowData.milestoneId;
      if (projectId && milestoneId) {
        const urlParams = new URLSearchParams();
        urlParams.append('projectId', projectId);
        if (projectName) urlParams.append('projectName', projectName);
        urlParams.append('editType', 'addInvoice');
        urlParams.append('milestoneId', milestoneId);
        window.open(`/finance/milestone-details?${urlParams.toString()}`, '_blank');
      } else {
        toast.error('Milestone details not found for this record.');
      }
    }
  }, [canAddMilestone, canEditMilestone]);

  const attachProjectDetailsClick = useCallback((rows = []) => (
    (Array.isArray(rows) ? rows : []).map((row) => {
      const projectId = row?.project_id || row?.projectId;
      const projectName = row?.project_name || row?.projectName;
      if (!projectId
        || !projectName
        || row?.project_name === 'Total' || row?.id === 'totals-row' || row?.is_child_row) {
        return row;
      }
      const existingRowConfiguration = row?.rowConfiguration || {};
      const existingOnClick = Array.isArray(existingRowConfiguration?.onClick)
        ? existingRowConfiguration.onClick
        : [];
      const projectClickTargets = ['project_name', 'projectName']
        .filter((field) => !existingOnClick.some((item) => item?.field === field))
        .map((field) => ({ field }));
      if (projectClickTargets.length === 0) {
        return row;
      }
      return {
        ...row,
        rowConfiguration: {
          ...existingRowConfiguration,
          onClick: [...existingOnClick, ...projectClickTargets],
        },
      };
    })
  ), []);

  const selectionColumnField = '__rowSelect';
  const getRowSelectionId = useCallback((row) => {
    if (!row || !row.milestone_id) return null;
    return `${row.__tableName || 'default'}_${row.milestone_id}`;
  }, []);

  const getSelectedRowIds = useCallback(
    (rows = []) => new Set(
      rows
        .map(getRowSelectionId)
        .filter(Boolean),
    ),
    [getRowSelectionId],
  );

  const rawTableDataRef = useRef({});
  const handleRowSelectionToggle = useCallback((event, rowId) => {
    if (!rowId) return;
    setSelectedRows((prev) => {
      const isSelected = event.target.checked;
      if (isSelected) {
        const rowData = rawTableDataRef.current[rowId];
        return prev.some((value) => getRowSelectionId(value) === rowId) ? prev : [...prev, rowData];
      }
      return prev.filter((value) => getRowSelectionId(value) !== rowId);
    });
  }, [getRowSelectionId]);

  const buildRowSelectionCell = useCallback((rowData, selectedIds) => {
    const rowId = getRowSelectionId(rowData);
    const isChecked = Boolean(rowId && selectedIds.has(rowId));
    return (
      <input
        type="checkbox"
        className="milestone-mis-row-checkbox"
        data-row-id={rowId || ''}
        defaultChecked={isChecked}
        onChange={(event) => handleRowSelectionToggle(event, rowId)}
      />
    );
  }, [getRowSelectionId, handleRowSelectionToggle]);

  const applySelectionToRows = useCallback((rows, selectedIds) => (
    (Array.isArray(rows) ? rows : []).map((row) => {
      if (row.project_name === 'Total' || row.id === 'totals-row' || row.is_child_row) {
        return { ...row, [selectionColumnField]: null };
      }
      const rowId = getRowSelectionId(row);
      if (rowId) rawTableDataRef.current[rowId] = row;
      return {
        ...row,
        [selectionColumnField]: buildRowSelectionCell(row, selectedIds),
      };
    })
  ), [buildRowSelectionCell, getRowSelectionId]);

  const buildSelectionColumn = useCallback((validRows, selectedIds) => {
    const filterableRows = validRows.filter(
      (row) => row.project_name !== 'Total' && row.id !== 'totals-row' && !row.is_child_row,
    );
    const rowIds = filterableRows
      .map(getRowSelectionId)
      .filter(Boolean);
    const allSelected = rowIds.length > 0 && rowIds.every((id) => selectedIds.has(id));
    return {
      field: selectionColumnField,
      header: (
        <input
          type="checkbox"
          className="milestone-mis-select-all"
          defaultChecked={allSelected}
          onChange={(event) => {
            const isChecked = event.target.checked;
            if (isChecked) {
              setSelectedRows((prev) => {
                const prevMap = new Map(prev.map((value) => [getRowSelectionId(value), value]));
                filterableRows.forEach((value) => prevMap.set(getRowSelectionId(value), value));
                return Array.from(prevMap.values());
              });
            } else {
              const validIds = new Set(
                filterableRows
                  .map(getRowSelectionId)
                  .filter(Boolean),
              );
              setSelectedRows((prev) => prev.filter((value) => !validIds.has(getRowSelectionId(value))));
            }
          }}
        />
      ),
      filter: false,
      sortable: false,
      pinnable: false,
      defaultPinned: true,
      isPinned: true,
      visible: true,
      showFilterButtonInHeader: false,
      showPinButtonInHeader: false,
      menuItems: [],
      className: 'milestone-selection-column',
    };
  }, [getRowSelectionId]);

  // Renders the raw React Node for the Action column
  const buildActionCell = useCallback((rowData) => {
    if (
      !rowData
      || rowData.project_name === 'Total'
      || rowData.projectName === 'Total'
      || String(rowData.project_name || rowData.projectName || '')
        .trim()
        .toLowerCase() === 'total'
      || rowData.id === 'totals-row'
      || rowData.is_total_row
      || rowData.isTotalRow
    ) {
      return null;
    }
    const rowActions = [];
    if (canEditMilestone) {
      rowActions.push('edit');
    }
    // Only parents get the ADD action
    if (!rowData.is_child_row && canAddMilestone) {
      rowActions.push('add');
    }

    if (rowActions.length === 0) {
      return null;
    }

    return (
      <div className="milestone-mis-action-cell">
        {rowActions.map((action) => (
          <button
            key={action}
            type="button"
            className={`milestone-mis-action-btn milestone-mis-action-btn--${action}`}
            title={action === 'add' ? 'Add Invoice' : 'Edit'}
            onClick={(event) => {
              event.stopPropagation();
              handleActionClick({ event, fieldPath: 'action', value: action, rowData, action });
            }}
          >
            {action === 'add'
              ? <span className="milestone-mis-action-icon milestone-mis-action-icon-add">+</span>
              : <i className="pi pi-pencil milestone-mis-action-icon milestone-mis-action-icon-edit" />}
          </button>
        ))}
      </div>
    );
  }, [canAddMilestone, canEditMilestone, handleActionClick]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    selectedRowsRef.current = selectedRows;
    const syncDOM = () => {
      const selectedIds = getSelectedRowIds(selectedRows);
      const rowCheckboxes = document.querySelectorAll('.milestone-mis-row-checkbox');
      rowCheckboxes.forEach((checkbox) => {
        const rowIdAttr = checkbox.getAttribute('data-row-id');
        const trimmedId = rowIdAttr ? rowIdAttr.trim() : null;
        checkbox.checked = Boolean(trimmedId && selectedIds.has(trimmedId));
      });
      const selectAllCheckboxes = document.querySelectorAll('.milestone-mis-select-all');
      selectAllCheckboxes.forEach((selectAllCheckbox) => {
        const table = selectAllCheckbox.closest('.p-datatable');
        if (!table) return;
        const tableRowCheckboxes = table.querySelectorAll('.milestone-mis-row-checkbox');
        const tableRowIds = Array.from(tableRowCheckboxes)
          .map((checkbox) => checkbox.getAttribute('data-row-id')?.trim())
          .filter(Boolean);
        const allSelectedInTable = tableRowIds.length > 0 && tableRowIds.every((id) => selectedIds.has(id));
        selectAllCheckbox.checked = allSelectedInTable;
        selectAllCheckbox.indeterminate = !allSelectedInTable && tableRowIds.some((id) => selectedIds.has(id));
      });
      tables.forEach((tbl) => {
        const headingStr = tbl.toolBarHeading;
        const countSpan = document.querySelector(`.selected-count-${headingStr.replace(/\s+/g, '-')}`);
        if (countSpan) {
          const count = selectedRows.filter((value) => value.__tableName === headingStr).length;
          countSpan.textContent = `Selected : ${count}`;
        }
      });

      // FE-ONLY FIX FOR RIGHT-ALIGNING AMOUNT HEADERS
      const thElements = document.querySelectorAll('.milestone-mis-filter-class th');
      thElements.forEach((th) => {
        const text = th.textContent || '';
        if (
          text.includes('Amount')
          || text.includes('Variance')
          || text.includes('%(Of Contract Value)')
          || text.includes('Projected Amount')
          || text.includes('Recognition Amount')
        ) {
          const contentWrapper = th.querySelector('.p-column-header-content');
          if (contentWrapper) {
            contentWrapper.style.justifyContent = 'flex-end';
            contentWrapper.style.textAlign = 'right';
          }
        }
      });
    };
    syncDOM();
    const timer = setTimeout(syncDOM, 100);
    return () => clearTimeout(timer);
  }, [selectedRows, getSelectedRowIds, tables, loading]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const counterId = 'monthly-milestone-total-project-counter';
    const injectCounter = () => {
      if (isInitialLoad || loading || totalRecords === 0) {
        const existingCounter = document.getElementById(counterId);
        if (existingCounter) existingCounter.remove();
        return;
      }
      const monthlyTable = document.querySelector('.custom-table-monthly-milestone-mis');
      if (monthlyTable) {
        const tableContainer = monthlyTable.closest('.p-datatable')
          || monthlyTable.closest('.table-utility')
          || monthlyTable;
        let counterDiv = document.getElementById(counterId);
        if (!counterDiv) {
          counterDiv = document.createElement('div');
          counterDiv.id = counterId;
          counterDiv.style.padding = '0px 10px 10px 10px';
          counterDiv.style.color = '#2B3245';
          counterDiv.style.fontFamily = 'mukta-bold, sans-serif';
          counterDiv.style.fontWeight = 'bold';
          counterDiv.style.fontSize = '16px';
          counterDiv.style.textAlign = 'left';
          counterDiv.style.width = '100%';
        }
        if (counterDiv.previousElementSibling !== tableContainer) {
          tableContainer.insertAdjacentElement('afterend', counterDiv);
        }
        const newText = `Total Project : ${totalRecords}`;
        if (counterDiv.textContent !== newText) {
          counterDiv.textContent = newText;
        }
      }
    };
    injectCounter();
    const interval = setInterval(injectCounter, 50);
    return () => clearInterval(interval);
  }, [isInitialLoad, loading, totalRecords, tables]);

  const getCustomToolbarHeading = (tblHeading) => {
    const isMonthly = tblHeading === 'Monthly Milestone MIS';
    return (
      <div className="custom-toolbar-wrapper">
        <span className="custom-toolbar-title">{tblHeading}</span>
        {!isMonthly && canBulkUpdate && (
          <div className="custom-toolbar-actions">
            <span className={`selected-count-label selected-count-${tblHeading.replace(/\s+/g, '-')}`}>
              Selected : 0
            </span>
            <button
              type="button"
              className="update-status-btn"
              onClick={() => {
                const currentSelected = selectedRowsRef.current.filter(
                  (value) => value.__tableName === tblHeading,
                );
                if (currentSelected.length === 0) {
                  toast.warning('Please select at least one milestone from this table');
                  return;
                }
                setRowsToUpdate(currentSelected);
                setShowBulkModal(true);
              }}
            >
              Update Status
            </button>
          </div>
        )}
      </div>
    );
  };

  const handleGlobalSortApply = async (field, order, direction) => {
    const newSort = { field, order, direction };
    await handleGlobalFilterApply(lastAppliedFiltersRef.current, newSort);
    setGlobalSort(newSort);
  };

  const handleGlobalFilterApply = async (appliedFilters, currentSort = globalSortRef.current) => {
    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;
    lastAppliedFiltersRef.current = appliedFilters;
    setGlobalFilters(appliedFilters || []);
    const currentUserProfile = userProfileRef.current;
    const filterPayloadArray = (appliedFilters || []).map((filter) => {
      const cleanFilter = { ...filter };
      delete cleanFilter.options; // Remove UI options to prevent PayloadTooLargeError
      return cleanFilter;
    });

    if (currentSort) {
      const sortIndex = filterPayloadArray.findIndex((value) => value.type === 'sort');
      if (sortIndex > -1) filterPayloadArray.splice(sortIndex, 1);
      filterPayloadArray.push({
        type: 'sort',
        field: currentSort.field,
        sortDirection: currentSort.direction,
        sortOrder: currentSort.order,
      });
    }
    const hasDateRange = filterPayloadArray.some(
      (value) => Array.isArray(value.range) && value.range.length === 2 && value.range[0] && value.range[1],
    );
    const hasValueFilters = filterPayloadArray.some(
      (value) => Array.isArray(value.selectedValues) && value.selectedValues.length > 0,
    );
    if (hasValueFilters && !hasDateRange) {
      toast.error('Please select a date range before applying other filters.', {
        position: 'top-center',
        style: { backgroundColor: '#bc4545', color: '#FFFFFF', borderRadius: '12px', fontFamily: 'Mukta' },
      });
      return false;
    }
    if (!isInitialLoad) {
      setTables((prev) => prev.map((tbl) => ({ ...tbl, data: [] })));
    }
    setLoading(true);
    const filterPayload = {
      filter: filterPayloadArray,
      ...(currentSort ? {
        sortField: currentSort.field,
        sortOrder: currentSort.direction || (currentSort.order === 1 ? 'ASC' : 'DESC'),
      } : {}),
      userInfo: {
        emailId: currentUserProfile.email,
        permissions: currentUserProfile.permissions,
        roleDetails: currentUserProfile.roleDetails,
        employeeId: currentUserProfile.employeeId,
      },
    };
    const hasActiveMilestoneDate = filterPayloadArray.some(
      (filterItem) => (
        filterItem.field === 'milestone_date.due_date'
        || filterItem.field === 'milestone_date.replanned_due_date'
      )
        && Array.isArray(filterItem.range)
        && filterItem.range[0]
        && filterItem.range[1],
    );
    const hasActiveInvoiceDate = filterPayloadArray.some(
      (filterItem) => (
        filterItem.field === 'revenue_invoice_date.invoice_date'
        || filterItem.field === 'revenue_invoice_date.revenue_date'
      )
        && Array.isArray(filterItem.range)
        && filterItem.range[0]
        && filterItem.range[1],
    );
    const showOrphanTable = !hasActiveMilestoneDate && hasActiveInvoiceDate;

    // UI Table Visibility Checks
    const activeTablesConfig = TABLES_CONFIG.filter((tbl) => {
      if (tbl.toolBarHeading === 'Monthly Milestone MIS' && !hasAmountViewAccess) {
        return false;
      }
      if (tbl.toolBarHeading === 'Invoices Without Planned Milestone') {
        return showOrphanTable;
      }
      return true;
    });

    try {
      let activeRecordCount = 0;
      const results = await Promise.all(
        activeTablesConfig.map(async (tbl) => {
          const res = await fetch(tbl.apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              token: currentUserProfile?.access_token || localStorage.getItem('authToken') || '',
            },
            body: JSON.stringify(filterPayload),
          });
          const json = await res.json();
          if (!res.ok) throw new Error(json?.message || 'Failed to load data. Please try again.');

          let tableData = json?.data?.tableData || [];
          if (needsMilestoneRegrouping(tableData)) {
            tableData = regroupMilestoneData(tableData);
          }

          // Strip the entire total row if the user lacks the milestone-amount-view permission
          if (!hasAmountViewAccess) {
            tableData = tableData.filter((row) => row.id !== 'totals-row' && row.project_name !== 'Total');
          }

          const initialRawTableData = tableData.map((value) => ({
            ...value,
            __tableName: tbl.toolBarHeading,
          }));
          const rawTableData = attachProjectDetailsClick(initialRawTableData);
          if (json?.meta?.total !== undefined && tbl.toolBarHeading === 'Monthly Milestone MIS') {
            activeRecordCount = json.meta.total;
          }

          let tableConfiguration = json?.data?.tableConfiguration || [];

          // COLUMN PERMISSIONS FILTERING
          const hiddenCols = [];
          if (!hasAmountViewAccess) hiddenCols.push('amount', 'total_due_amount');
          if (!hasInvoiceAmountViewAccess)
            hiddenCols.push('invoice_amount', 'invoice_amount_with_tax', 'variance', 'total_recognition_amount');
          if (!hasPoNumberViewAccess) hiddenCols.push('po_number');
          if (!hasPoLinkViewAccess) hiddenCols.push('po_link');
          if (!hasReplannedDueDateViewAccess) hiddenCols.push('replanned_due_date');
          if (!hasReceivedDateViewAccess) hiddenCols.push('received_date');
          if (!hasRemarksViewAccess) hiddenCols.push('remarks');

          // Completely remove the action column if user lacks both add and edit permissions
          if (!canShowMilestoneActions) hiddenCols.push('action');

          tableConfiguration = tableConfiguration.filter(col => {
            if (hiddenCols.includes(col.field)) return false;
            // Catch dynamic month columns for amount/invoice_amount
            if (!hasAmountViewAccess && col.field.startsWith('month_due_amount')) return false;
            if (!hasInvoiceAmountViewAccess && col.field.startsWith('month_recognition_amount')) return false;
            return true;
          });

          // Handle nested formats (e.g., replanned_due_date inside milestone_date)
          tableConfiguration = tableConfiguration.map(col => {
            if (col.field === 'milestone_date' && col.format?.template) {
              let newTemplate = col.format.template;
              if (!hasReplannedDueDateViewAccess)
                newTemplate = newTemplate.filter(value => value.field !== 'replanned_due_date');
              return { ...col, format: { ...col.format, template: newTemplate } };
            }
            return col;
          });

          // DP2-6676 Fix: Inject options to allow View Applied Filters to map IDs to labels properly
          if (tableConfiguration && Array.isArray(tableConfiguration)) {
            tableConfiguration = tableConfiguration.map((col) => {
              if (col.menuItems && Array.isArray(col.menuItems)) {
                col.menuItems = col.menuItems.map((menuGroup) => {
                  if (menuGroup.items && Array.isArray(menuGroup.items)) {
                    menuGroup.items = menuGroup.items.map((item) => {
                      if (item.type === 'filter') {
                        const lookups = filterLookupOptionsRef.current || {};
                        if (item.field === 'project_name' && lookups.projectNames?.length > 0) {
                          item.options = lookups.projectNames;
                        } else if (item.field === 'customer_name' && lookups.clientNames?.length > 0) {
                          item.options = lookups.clientNames;
                        } else if (item.field === 'project_vertical' && lookups.verticalNames?.length > 0) {
                          item.options = lookups.verticalNames;
                        } else if (item.field === 'sr_manager' && lookups.projectManagers?.length > 0) {
                          item.options = lookups.projectManagers;
                        } else if (item.field === 'milestone_status_name' && lookups.milestoneStatuses?.length > 0) {
                          item.options = lookups.milestoneStatuses;
                        } else if (item.field === 'invoice_status_name' && lookups.invoiceStatuses?.length > 0) {
                          item.options = lookups.invoiceStatuses;
                        } else if (item.field === 'milestone_type_name' && lookups.milestoneTypes?.length > 0) {
                          item.options = lookups.milestoneTypes;
                        } else if (item.field === 'invoice_type_name' && lookups.invoiceTypes?.length > 0) {
                          item.options = lookups.invoiceTypes;
                        } else if (item.field === 'zoho_client_name_milestone' && lookups.zohoClients?.length > 0) {
                          item.options = lookups.zohoClients;
                        } else if (item.field === 'zoho_client_name_invoice' && lookups.zohoClients?.length > 0) {
                          item.options = lookups.zohoClients;
                        } else if (item.field === 'project_status' && lookups.projectStatuses?.length > 0) {
                          item.options = lookups.projectStatuses;
                        } else if (item.field === 'po_number' && lookups.poNumbers?.length > 0) {
                          item.options = lookups.poNumbers;
                        }
                      }
                      return item;
                    });
                  }
                  return menuGroup;
                });
              }
              return col;
            });
          }

          // EXPLICIT CLASS INJECTION AND COLUMN WIDTH ENFORCEMENT
          const metricWidthMap = {
            cont_value_p: 160,
            amount: 220,
            cogs: 190,
            ga: 190,
            tp: 190,
            due_date: 140,
            replanned_due_date: 160,
            invoice_amount: 160,
            invoice_cogs: 150,
            invoice_ga: 150,
            invoice_tp: 150,
            invoice_amount_with_tax: 200,
            variance: 150,
            billed_effort: 140,
          };

          tableConfiguration = tableConfiguration.map((col) => {
            let updatedCol = { ...col };

            if (metricWidthMap[col.field]) {
              const assignedWidth = metricWidthMap[col.field];
              updatedCol = {
                ...updatedCol,
                width: assignedWidth,
                minWidth: assignedWidth,
                className: `${updatedCol.className || ''} col-${col.field}`.trim(),
                headerClassName: `${updatedCol.headerClassName || ''} col-${col.field}`.trim(),
                bodyClassName: `${updatedCol.bodyClassName || ''} col-${col.field}`.trim(),
              };
            }

            if (col.field === 'sr_manager') {
              return {
                ...updatedCol,
                className: 'col-left-align col-ellipsis col-manager-name',
                bodyClassName: 'col-manager-name',
                headerClassName: 'col-manager-name',
              };
            }
            if (col.field === 'remarks') {
              return {
                ...updatedCol,
                className: 'col-left-align col-ellipsis col-remarks',
                bodyClassName: 'col-remarks',
                headerClassName: 'col-remarks',
              };
            }
            if (col.field === 'action' || col.type === 'action') {
              const allowedActions = [];
              if (canEditMilestone) allowedActions.push('edit');
              if (canAddMilestone) allowedActions.push('add');
              return {
                ...updatedCol,
                type: 'string', // Treats this as a mapped React Node, skipping TableUtility defaults
                actions: allowedActions,
                visible: allowedActions.length > 0,
                width: allowedActions.includes('add') ? 160 : col.width,
              };
            }
            return updatedCol;
          });

          const tableRowCount = Math.max(rawTableData.length, 1);
          const isGroupedTable = tbl.toolBarHeading === 'Monthly Milestone MIS';
          const isOrphanTable = tbl.toolBarHeading === 'Invoices Without Planned Milestone';
          const selectedIds = getSelectedRowIds(selectedRows);

          const renderCheckboxes = !isGroupedTable && canBulkUpdate;
          const exportFileName = getMilestoneExportFileName(tbl.toolBarHeading);
          const hasTotalRow = rawTableData.some(
            (row) => row?.id === 'totals-row' || row?.project_name === 'Total',
          );

          const tableClassName = `custom-table-${tbl.toolBarHeading.replace(/\s+/g, '-').toLowerCase()}`
            + ` ${renderCheckboxes ? 'has-checkboxes' : ''}`
            + ` ${canShowMilestoneActions ? 'has-edit-columns' : ''}`
            + ` ${hasTotalRow ? 'has-total-row' : ''}`;

          const tableDataWithSelection = renderCheckboxes
            ? applySelectionToRows(rawTableData, selectedIds)
            : rawTableData;

          // Inject the raw HTML action cell so TableUtility renders it blindly
          const tableDataWithActions = tableDataWithSelection.map((row) => ({
            ...row,
            action: buildActionCell(row),
          }));

          const finalTableConfiguration = renderCheckboxes
            ? [buildSelectionColumn(rawTableData, selectedIds), ...tableConfiguration]
            : tableConfiguration;

          return {
            configuration: {
              generalSettings: {
                enableApiFiltering: false,
                baseURL: tbl.apiUrl,
                showToolBar: true,
                showPagination: false,
                handler: {
                  onCellClickHandler: handleActionClick,
                  onFilterUpdate: ({ action, filters }) => {
                    if (action === 'reset') {
                      handleGlobalFilterApply([]);
                      return;
                    }
                    if (action === 'apply') {
                      handleGlobalFilterApply(filters || [], globalSortRef.current);
                    }
                  },
                },
                toolBarSettings: {
                  showToolBarHeading: true,
                  toolBarHeading: getCustomToolbarHeading(tbl.toolBarHeading),
                  showDownloadCsv: true,
                  showDownloadExcel: false,
                  downloadCsvFileName: exportFileName,
                  downloadExcelFileName: exportFileName,
                  downloadExcelSheetName: tbl.toolBarHeading,
                },
                userInfo: {
                  emailId: currentUserProfile.email,
                  permissions: currentUserProfile.permissions,
                  roleDetails: currentUserProfile.roleDetails,
                  employeeId: currentUserProfile.employeeId,
                },
                headers: {
                  token: currentUserProfile?.access_token || localStorage.getItem('authToken') || '',
                },
              },
              apiEndpoints: {
                getAllViews: { endpoint: '', enabled: true },
                getFilteredData: { endpoint: '', enabled: true },
                saveOrUpdateView: { endpoint: 'view', enabled: true },
                deleteView: { endpoint: 'view-delete', enabled: false },
                download: {
                  endpoint: isOrphanTable ? 'orphan-export' : 'export',
                  enabled: true,
                  filename: exportFileName,
                  sheetName: tbl.toolBarHeading,
                },
              },
              tableSettings: {
                enableTreeTable: false,
                useVirtualizedTable: false,
                size: 'small',
                pageSize: tableRowCount,
                showPageSizeSelector: false,
                paginationConfig: {
                  page: 1,
                  limit: tableRowCount,
                  rowsPerPageOptions: [tableRowCount],
                },
                showExpandCollapse: false,
                tableConfig: finalTableConfiguration,
                maxFrozenColumns: { desktop: 4, tablet: 2, mobile: 1 },
                tableClassName,
                emptyMessage: ' ',
              },
            },
            data: tableDataWithActions,
            rawTableData,
            toolBarHeading: tbl.toolBarHeading,
            useCustomBodyRenderer: tbl.useCustomBodyRenderer ?? false,
          };
        }),
      );
      if (currentRequestId === requestIdRef.current) {
        setTotalRecords(activeRecordCount);
        setTables(results);
      }
      return true;
    } catch (err) {
      if (currentRequestId === requestIdRef.current) {
        toast.error(err.message || 'An error occurred. Please try again.', {
          position: 'top-center',
          style: {
            backgroundColor: '#bc4545',
            color: '#FFFFFF',
            borderRadius: '12px',
            fontFamily: 'Mukta',
          },
        });
      }
      return false;
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setIsInitialLoad(false);
        setLoading(false);
      }
    }
  };

  const handleBulkUpdateSubmit = async (updateData) => {
    setIsUpdating(true);
    try {
      const baseUrl = getMilestoneApiUrl().replace(MIS_MILESTONE, '');
      const url = `${baseUrl}${MIS_MILESTONE}/bulk-update`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          token: userProfile?.access_token || localStorage.getItem('authToken') || '',
        },
        body: JSON.stringify({
          milestoneIds: rowsToUpdate
            .map((value) => value.milestone_id)
            .filter(Boolean),
          ...updateData,
        }),
      });
      const json = await response.json();
      if (response.ok && json.success) {
        toast.success(json.message || 'Milestones updated successfully');
        setShowBulkModal(false);
        const updatedIds = new Set(
          rowsToUpdate.map((value) => getRowSelectionId(value)),
        );
        setSelectedRows((prev) => prev.filter((value) => !updatedIds.has(getRowSelectionId(value))));
        setRowsToUpdate([]);
        handleGlobalFilterApply(lastAppliedFiltersRef.current, globalSortRef.current);
      } else {
        toast.error(json.message || 'Failed to update milestones');
      }
    } catch (error) {
      toast.error('An error occurred while updating milestones');
    } finally {
      setIsUpdating(false);
    }
  };

  const poMisGlobalFilters = (tables[0]?.configuration?.tableSettings?.tableConfig || [])
    .filter((col) => col.isGlobalFilter);
  const monthlyPoMisGlobalFilters = (tables[1]?.configuration?.tableSettings?.tableConfig || [])
    .filter((col) => col.isGlobalFilter);

  const desiredFilterOrder = [
    'project_name',
    'customer_name',
    'project_status',
    'project_vertical',
    'project_type',
    'sr_manager',
    'po_number',
    'milestone_status_name',
    'invoice_status_name',
    'milestone_date',
    'revenue_invoice_date',
    'milestone_type_name',
    'invoice_type_name',
    'zoho_client_name_milestone',
    'zoho_client_name_invoice',
  ];

  const allGlobalFilterColumns = [...poMisGlobalFilters, ...monthlyPoMisGlobalFilters]
    .filter(
      (col, _idx, arr) => arr.findIndex((value) => value.field === col.field) === arr.indexOf(col),
    )
    .sort((columnA, columnB) => {
      const indexA = desiredFilterOrder.indexOf(columnA.field);
      const indexB = desiredFilterOrder.indexOf(columnB.field);
      const posA = indexA === -1 ? 999 : indexA;
      const posB = indexB === -1 ? 999 : indexB;
      return posA - posB;
    });

  const allTablesEmpty = tables.every((tbl) => !tbl.data || tbl.data.length === 0);

  const hasActiveMilestoneDate = globalFilters.some(
    (filterItem) => (
      filterItem.field === 'milestone_date.due_date'
      || filterItem.field === 'milestone_date.replanned_due_date'
    )
      && Array.isArray(filterItem.range)
      && filterItem.range[0]
      && filterItem.range[1],
  );
  const hasActiveInvoiceDate = globalFilters.some(
    (filterItem) => (
      filterItem.field === 'revenue_invoice_date.invoice_date'
      || filterItem.field === 'revenue_invoice_date.revenue_date'
    )
      && Array.isArray(filterItem.range)
      && filterItem.range[0]
      && filterItem.range[1],
  );

  const expectedTableCount = [
    hasAmountViewAccess,
    true,
    (!hasActiveMilestoneDate && hasActiveInvoiceDate),
  ].filter(Boolean).length;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleDblClick = (event) => {
      const cellContent = event.target.closest('.tu-cell-content');
      if (cellContent) {
        const range = document.createRange();
        range.selectNodeContents(cellContent);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
    };

    // 2. Unified Tooltip Handler for Headers and Data Cells
    const handleMouseOver = (event) => {
      // Looks for either the hidden header cell text or the row data text cell
      const targetElement = event.target.closest('.tu-header-line, .tu-cell-content');

      if (targetElement) {
        // Check if the text is physically truncated/cut off by layout limits
        const isTruncated = targetElement.scrollWidth > targetElement.clientWidth;

        if (isTruncated) {
          // Read the full text string and map it to a standard tooltip popup
          targetElement.setAttribute('title', targetElement.textContent.trim());
        } else {
          // Remove it if the column is wide enough to display the text fully
          targetElement.removeAttribute('title');
        }
      }
    };

    // Bind the events cleanly inside the container element
    container.addEventListener('dblclick', handleDblClick);
    container.addEventListener('mouseover', handleMouseOver);

    // Cleanup: Cleans up listeners during re-renders to prevent memory leaks
    return () => {
      container.removeEventListener('dblclick', handleDblClick);
      container.removeEventListener('mouseover', handleMouseOver);
    };
  }, []);

  return (
    <div ref={containerRef} className="milestone-mis-filter-class filter-class">
      <TableUtility
        tables={tables.map((tbl) => ({
          configuration: tbl.configuration,
          data: tbl.data,
          useCustomBodyRenderer: typeof tbl.useCustomBodyRenderer === 'boolean'
            ? tbl.useCustomBodyRenderer
            : false,
        }))}
        globalFilters={globalFilters}
        globalFilterConfig={allGlobalFilterColumns}
        showGlobalFilter
        showFilterOption
        showTieredMenu
        showDownloadCSV
        showDownloadExcel={false}
        showMultiTableExport={false}
        onGlobalFilterApply={handleGlobalFilterApply}
        onGlobalSortApply={handleGlobalSortApply}
        emptyStateComponent={
          (isInitialLoad && loading)
            ? (
              <div className="empty-state-container loading">
                <img src={loadingPuzzleGif} alt="Loading..." className="loading-puzzle-gif" />
                <span className="loading-state-text">
                  Loading milestone details...
                </span>
              </div>
            )
            : (loading && !isInitialLoad)
              ? <LocalSkeleton count={expectedTableCount} />
              : (allTablesEmpty ? (
                globalFilters.length > 0 ? (
                  <div className="empty-state-container">
                    <i className="pi pi-inbox empty-state-icon"></i>
                    <span className="empty-state-text">
                      No data found for the selected filter(s).
                    </span>
                  </div>
                ) : <MilestoneInitial />
              ) : undefined)
        }
        globalFilterAlignRight
        renderGlobalFilterTrigger={(globalFilterTrigger) => (
          <div className="milestone-mis-page-header">
            <span>Milestone MIS</span>
            <div className="milestone-mis-page-header-actions">
              {globalFilterTrigger}
            </div>
          </div>
        )}
      />
      {showBulkModal && (
        <ChangeMilestoneModal
          milestoneStatuses={milestoneStatuses}
          invoiceStatuses={invoiceStatuses}
          onUpdate={handleBulkUpdateSubmit}
          onCancel={() => {
            setShowBulkModal(false);
            setRowsToUpdate([]);
          }}
          isLoading={isUpdating}
        />
      )}
    </div>
  );
};

export default MisMilestone;
